'use client';

import PropTypes from 'prop-types';
import {enqueueSnackbar} from "notistack";
import {useState, useCallback} from "react";
import {useForm, Controller} from "react-hook-form";

import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Rating from "@mui/material/Rating";
import {styled} from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import {paths} from "src/routes/paths";
import {useRouter} from "src/routes/hooks";

import useUserRatingDataForRecipe from "src/hooks/rating/use-user-rating-data-for-recipe";

import {useAuthContext} from "src/auth/hooks";
import {saveRating} from "src/services/rating/save-api";
import {getThumbnailUrl} from "@/utils/image-utils";

const STAR_LABELS = {
  1: 'Beznadziejny',
  2: 'Zły',
  3: 'Słaby',
  4: 'Dostateczny',
  5: 'Dobry',
  6: 'Bardzo dobry',
  7: 'Znakomity',
  8: 'Wyśmienity',
  9: 'Arcydzieło',
  10: 'Boski'
};

const StyledRating = styled(Rating)(({theme}) => ({
  [theme.breakpoints.up("md")]: {
    maxWidth: 'calc(100% - 45px)'
  },
}));

function RecipeReview({recipeData}) {
  const router = useRouter();
  const {user} = useAuthContext();
  const [hoverRating, setHoverRating] = useState(null);

  const methods = useForm({
    defaultValues: {
      rating: 0
    }
  });

  const {
    control,
    watch,
    setValue
  } = methods;

  useUserRatingDataForRecipe(recipeData.pimcoreRecipeId, useCallback((data) => {
    if (data) {
      setValue('rating', data.ratingValue);
    }
  }, [setValue]));

  const rating = hoverRating > 0 ? hoverRating : watch('rating');
  const starLabel = STAR_LABELS[rating];

  const onSubmit = async (value) => {
    if (!user || user.isLoggedIn === false) {
      enqueueSnackbar('Musisz być zalogowany, aby ocenić przepis.', {variant: 'error'});
      router.push(paths.auth.token.loginWithReturnTo(paths.recipe.details(recipeData.pimcoreRecipeId)));
    } else {
      await sendRatingForm(value);
      enqueueSnackbar('Dziękujemy za dodanie oceny tego przepisu. Twoja ocena została zapisana!');
    }
  };

  const sendRatingForm = async (ratingValue) => {
    try {
      return await saveRating(ratingValue, recipeData.pimcoreRecipeId);
    } catch (ex) {
      if (!!ex && !!ex.message) {
        enqueueSnackbar(`Error. Could not update user. Details: ${  ex.message}`, { variant: `error` });
      } else {
        enqueueSnackbar('Error. Could not update user.', { variant: `error` });
      }

      throw ex;
    }
  };

  return (
    <Stack
      component={Card}
      variant="outlined"
      spacing={1}
      p={2}
    >
      <Stack
        spacing={2}
        direction="row"
        alignItems="center"
      >
        <Avatar
          src={getThumbnailUrl(user?.photo, '270_270')}
          alt={user?.name}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {user?.name?.charAt(0).toUpperCase()}
        </Avatar>
        {rating > 0 && <>
          <Typography variant="body1">Twoja ocena:</Typography>
          <Typography variant="body1" color="text.secondary">{starLabel}</Typography>
        </>}
        {rating <= 0 && <Typography variant="body1" color="text.secondary">Oceń ten przepis</Typography>}
      </Stack>
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={{xs: 1, md: 0}}
        alignItems="center"
        justifyContent="space-between"
      >

        <Controller
          name="rating"
          control={control}
          render={({field}) => (
            <StyledRating
              {...field}
              max={10}
              value={Number(field.value)}
              onChange={(event, newValue) => {
                field.onChange(newValue);
                onSubmit(newValue);
              }}
              onChangeActive={(event, newValue) => {
                setHoverRating(newValue);
              }}
            />
          )}
        />
        <Typography variant="body1" color="text.secondary" sx={{}}>{rating}/10</Typography>
      </Stack>
    </Stack>
  );
}

RecipeReview.propTypes = {
  recipeData: PropTypes.object
}

export default RecipeReview;
